import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { Trans, useTranslation } from "@coworker/locales";
import { Article } from "../../types/article";
import { filterMainArticles } from "../../Helpers/articleHelpers";
import { ModalTypes } from "../../types/views";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import {
  LeftContainer,
  ListItemContainer,
  RightContainer,
  SizedText,
  TextStyle,
} from "../../styles/styles";
import { Groups } from "../../types/groups";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon"; // NOSONAR
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath"; // NOSONAR
import { logEvent } from "../../../../helpers/tracker";
import { useStoreId } from "../../../../core/auth/useLoggedInUser";
import { generateLivligProductsLink } from "@coworker/reusable/helpers/livligIntegration";

interface SelectedArticlesActionsModalProps {
  selectedArticles: Article[];
  modalType: ModalTypes;
  onTriggerNewModal: (modalType: ModalTypes) => void;
  groups: Groups;
}

export const SelectedArticlesActionsModal = ({
  selectedArticles,
  modalType,
  onTriggerNewModal,
  groups,
}: SelectedArticlesActionsModalProps) => {
  const { t } = useTranslation();
  const storeId = useStoreId();

  const logEventForModalType = (modalType: ModalTypes) => {
    switch (modalType) {
      case ModalTypes.PTAG_ARTICLE_NUMBERS:
        logEvent("ce:articles_selected_copyAllArticleNumbers");
        break;
      case ModalTypes.CREATE_GROUP:
        logEvent("ce:articles_selected_creategroup");
        break;
      case ModalTypes.ADD_TO_GROUP:
        logEvent("ce:articles_selected_addtogroup");
        break;
      case ModalTypes.DELETE_SELECTED_ARTICLES:
        logEvent("ce:articles_selected_deletearticles");
        break;
      default:
    }
  };

  const openLivlig = () => {
    const articleNumbers = selectedArticles
      .map((article) => article.productArticleId)
      .join(",");
    const url = generateLivligProductsLink(storeId, articleNumbers);
    if (url) {
      window.open(url, "_blank");
    }
  };
  const filteredMainArticles = filterMainArticles(selectedArticles);
  const actionMenuChoice = (
    text: string,
    modalType: ModalTypes,
    disabled: boolean
  ) => {
    if (modalType === ModalTypes.ADD_TO_GROUP && (groups?.length ?? 0) === 0) {
      return null;
    }
    return (
      <FixaListViewItem
        onClick={() => {
          if (!disabled) {
            logEventForModalType(modalType);
            onTriggerNewModal(modalType);
          }
        }}
        title={
          <SizedText
            style={{ margin: "0px 24px" }}
            $fontSize={16}
            $bold={!disabled}
            onClick={() => {
              logEventForModalType(modalType);
              onTriggerNewModal(modalType);
            }}
          >
            <Trans>{text}</Trans>
          </SizedText>
        }
        emphasised
        size="small"
      />
    );
  };

  return (
    <FixaModal
      visible={[
        ModalTypes.GROUP_SELECTED_ARTICLES_ACTIONS,
        ModalTypes.SELECTED_ARTICLES_ACTIONS,
      ].includes(modalType)}
      handleCloseBtn={() => {
        onTriggerNewModal(ModalTypes.KEEP_SELECTION);
      }}
    >
      <FixaSheets
        labelledById="selectedArticlesActions"
        size={"small"}
        header={
          <FixaModalHeader
            ariaCloseTxt="Close"
            title={`${filteredMainArticles?.length ?? 0} ${t(
              "articlesSelectedString"
            )}`}
          />
        }
        footer={
          <FixaModalFooter>
            <></>
          </FixaModalFooter>
        }
      >
        <FixaListView id="selectedArticlesActionsId" size="small">
          {actionMenuChoice(
            "createPTagPrintListString",
            ModalTypes.PTAG_ARTICLE_NUMBERS,
            false
          )}
          {modalType === ModalTypes.SELECTED_ARTICLES_ACTIONS &&
            actionMenuChoice(
              "createGroupString",
              ModalTypes.CREATE_GROUP,
              false
            )}
          {modalType === ModalTypes.SELECTED_ARTICLES_ACTIONS &&
            actionMenuChoice(
              "addToGroupString",
              ModalTypes.ADD_TO_GROUP,
              (groups?.length ?? 0) === 0
            )}
          <FixaListViewItem
            onClick={() => {
              openLivlig();
            }}
            title={
              <SizedText
                style={{ margin: "0px 24px" }}
                $fontSize={16}
                $bold={true}
                onClick={() => {
                  openLivlig();
                }}
              >
                <Trans>livligViewInString</Trans>
              </SizedText>
            }
            emphasised
            size="small"
          />
          <ListItemContainer
            onClick={() => {
              logEventForModalType(ModalTypes.DELETE_SELECTED_ARTICLES);
              onTriggerNewModal(ModalTypes.DELETE_SELECTED_ARTICLES);
            }}
          >
            <LeftContainer>
              <div style={{ justifyContent: "left" }}>
                <TextStyle
                  $bold={true}
                  $italic={false}
                  $size={"medium"}
                  $color="pink"
                >
                  <Trans>deleteSelectedArticlesString</Trans>
                </TextStyle>
              </div>
            </LeftContainer>
            <RightContainer>
              <FixaSSRIcon paths={TrashCanIconPath} color="var(--pink)" />
            </RightContainer>
          </ListItemContainer>
        </FixaListView>
      </FixaSheets>
    </FixaModal>
  );
};
